import { useEffect, useRef, type ReactNode, useState, useCallback } from 'react';
import { Box, Flex, Tab, TabList, Tabs, Badge, Spinner } from '@chakra-ui/react';
import { useInView } from 'react-intersection-observer';
import { Landmark } from 'lucide-react';
import { TicketListSkeleton } from './TicketListSkeleton';
import { TicketListItem } from './TicketListItem';
import { EmptyTicketList } from './EmptyTicketList';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

type TicketListTab = {
  key: string;
  label: ReactNode;
  count?: number;
  isActive?: boolean;
  onClick?: () => void;
};

type Ticket = {
  id: string;
  requesterName: string;
  requesterType: string;
  subject: string;
  relativeTime: string;
  hasUnread: boolean;
  assignedToName?: string;
  isProviderTicket?: boolean;
};

type TicketListProps = {
  tabs: TicketListTab[];
  tickets: Ticket[];
  baseUrl: string;
  selectedTicketId?: string;
  loading?: boolean;
  skeletonCount?: number;
  showAssignedTo?: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onEndReached?: () => void;
};

export function TicketList({
  tabs,
  tickets,
  baseUrl,
  selectedTicketId,
  loading = false,
  skeletonCount = 8,
  showAssignedTo = false,
  hasMore = false,
  isFetchingMore = false,
  onEndReached,
}: TicketListProps) {
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const handleSetScrollRoot = useCallback((node: HTMLElement | null) => {
    setScrollRoot(node ?? null);
  }, []);

  const { ref: sentinelRef, inView } = useInView({
    root: scrollRoot,
    rootMargin: '100px',
    threshold: 0,
  });

  const wasInViewRef = useRef(false);

  useEffect(() => {
    const enteredView = inView && !wasInViewRef.current;

    if (!loading && enteredView && hasMore && !isFetchingMore) {
      onEndReached?.();
    }

    wasInViewRef.current = inView;
  }, [loading, inView, hasMore, isFetchingMore, onEndReached]);

  return (
    <Flex
      direction="column"
      minW={pxToRem(420)}
      maxW={pxToRem(420)}
      border="1px"
      borderStyle="solid"
      borderColor="border.default"
      borderTopWidth={0}
      bg="bg.surface"
      boxShadow="0.25rem 0 0.25rem rgb(0 0 0 / 8%)"
      h="full"
    >
      <Tabs
        variant="unstyled"
        index={tabs.findIndex((tab) => tab.isActive === true)}
        color="fg.default"
      >
        <TabList borderBottom="1px" borderColor="border.default" bg="bg.subtle" display="flex">
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              onClick={tab.onClick}
              borderBottom={tab.isActive === true ? '2px' : undefined}
              borderStyle="solid"
              borderColor={tab.isActive === true ? 'blue.500' : undefined}
              bg={tab.isActive === true ? 'bg.surface' : 'bg.subtle'}
              fontSize="md"
              pb={3}
              borderRadius="0"
              flex="1"
              cursor="pointer"
              _hover={{ bg: tab.isActive === true ? 'bg.surface' : 'bg.muted' }}
            >
              <Flex align="center" gap={2}>
                {tab.label}

                {tab.count !== undefined && (
                  <Badge
                    bg="blue.500"
                    color="white"
                    borderRadius="full"
                    fontSize="2xs"
                    minW="1.125rem"
                    h="1.125rem"
                    px={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {tab.count}
                  </Badge>
                )}
              </Flex>
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <Box ref={handleSetScrollRoot} flex="1" overflowY="auto">
        {loading ? (
          Array.from({ length: skeletonCount }).map((_, i) => <TicketListSkeleton key={i} />)
        ) : tickets.length > 0 ? (
          <Box>
            {tickets.map((ticket) => (
              <TicketListItem
                key={ticket.id}
                id={ticket.id}
                baseUrl={baseUrl}
                requesterName={ticket.requesterName}
                requesterType={ticket.requesterType}
                subject={ticket.subject}
                relativeTime={ticket.relativeTime}
                hasUnread={ticket.hasUnread}
                assignedToName={showAssignedTo ? ticket.assignedToName : undefined}
                requesterTypeIcon={
                  ticket.isProviderTicket === true ? (
                    <Landmark size={iconSize.sm} strokeWidth={2} />
                  ) : undefined
                }
                bg={ticket.id === selectedTicketId ? 'bg.subtle' : undefined}
              />
            ))}

            {hasMore ? (
              <Flex ref={sentinelRef} justify="center" py={3}>
                {isFetchingMore ? <Spinner size="sm" /> : null}
              </Flex>
            ) : null}
          </Box>
        ) : (
          <EmptyTicketList />
        )}
      </Box>
    </Flex>
  );
}
